import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Select, DatePicker, Button, Table, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import * as XLSX from "xlsx";
import { getStatesByRole, getCleaningAuditRoleId, getFacilityAuditCompletion } from "./cleaningAuditApi";

const { Option } = Select;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const pctColor = (pct) => {
  if (pct >= 80) return "#2E7D4F";
  if (pct >= 50) return "#A66423";
  if (pct > 0) return "#A63636";
  return undefined;
};

// Matches auditScheduled/AuditRegister.js: flat rows of {facility_id, facility_name,
// month, completion_percentage} grouped client-side into one row per facility.
const AuditRegister = () => {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [year, setYear] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const roleId = await getCleaningAuditRoleId();
        const { error, result } = await getStatesByRole(roleId);
        setStates(!error && Array.isArray(result?.result) ? result.result : []);
      } catch (e) {
        setStates([]);
        message.error({ key: "cleaning-audit-connect", content: "Couldn't connect to Cleaning Audit. Please try again shortly." });
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    rows.forEach((item) => {
      if (!map[item.facility_id]) {
        map[item.facility_id] = { facility_id: item.facility_id, facility_name: item.facility_name, months: {} };
      }
      map[item.facility_id].months[item.month] = item;
    });
    return Object.values(map).sort((a, b) => a.facility_name.localeCompare(b.facility_name));
  }, [rows]);

  const columns = useMemo(
    () => [
      { title: "Facility Name", dataIndex: "facility_name", key: "facility_name", fixed: "left" },
      ...MONTHS.map((label, i) => ({
        title: label,
        key: `m${i + 1}`,
        render: (_, row) => {
          const m = row.months[i + 1];
          if (!m) return "-";
          return <span style={{ color: pctColor(m.completion_percentage), fontWeight: 600 }}>{m.completion_percentage}%</span>;
        },
      })),
    ],
    []
  );

  const search = useCallback(async () => {
    if (!stateId) {
      message.error("Please select a state first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    const { error, result } = await getFacilityAuditCompletion({ year: Number(year.format("YYYY")), state_id: Number(stateId) });
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [stateId, year]);

  const exportExcel = useCallback(() => {
    const header = ["Facility Name", ...MONTHS];
    const data = grouped.map((f) => [
      f.facility_name,
      ...MONTHS.map((_, i) => (f.months[i + 1] ? `${f.months[i + 1].completion_percentage}%` : "-")),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Audit Register");
    const stateName = states.find((s) => s.state_id === stateId)?.state_name || "All";
    XLSX.writeFile(wb, `${year.format("YYYY")}_${stateName.replace(/\s+/g, "_")}_Audit_Register.xlsx`);
  }, [grouped, year, stateId, states]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Year</div>
            <DatePicker
              picker="year"
              value={year}
              onChange={(d) => d && setYear(d)}
              allowClear={false}
              disabledDate={(d) => d && d > moment().endOf("year")}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a state"
              style={{ width: 220 }}
              value={stateId}
              onChange={setStateId}
            >
              {states.map((s) => (
                <Option key={s.state_id} value={s.state_id}>{s.state_name}</Option>
              ))}
            </Select>
          </div>
          <Button type="primary" loading={loading} onClick={search}>Search</Button>
          {grouped.length > 0 && (
            <Button icon={<DownloadOutlined />} onClick={exportExcel}>Export</Button>
          )}
        </div>
      </div>

      {searched && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, margin: "8px 0 12px" }}>Audit Completion Report</div>
          <Table
            rowKey="facility_id"
            columns={columns}
            dataSource={grouped}
            loading={loading}
            scroll={{ x: true }}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: "No records for this year/state." }}
          />
        </div>
      )}
    </div>
  );
};

export default memo(AuditRegister);
